import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * PdfPreviewContext — estado global del popup de vista previa de PDF de examen.
 *
 * Motivo: el popup se abre desde el iframe del visualizador (que vive dentro
 * del Dialog del viewer). Un Dialog MUI anidado dentro de otro Dialog queda
 * atrapado en el stacking del padre y aparece POR DEBAJO del viewer en el
 * build de prod, sin importar el z-index. Mismo problema que tuvo el popup de
 * ácido-base ([[AcidBaseContext]]).
 *
 * Solución: elevar el estado acá y renderizar el PdfPreviewDialog a nivel App
 * (ver App.js), fuera del viewer, igual que el popup de cultivos y el de
 * ácido-base. El listener `open-exam-pdf` en ExamsVisualizer llama
 * `openPdfPreview(payload)` con la url del PDF local + metadata del examen.
 */

const PdfPreviewContext = createContext(null);

export function PdfPreviewProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [pdf, setPdf] = useState(null); // { url, name, date, value, page }

  const openPdfPreview = useCallback((payload) => {
    if (!payload || !payload.url) return;
    setPdf({
      url: payload.url,
      name: payload.name || "",
      date: payload.date || "",
      value: payload.value || "",
      page: payload.page || "",
    });
    setOpen(true);
  }, []);

  const closePdfPreview = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ open, pdf, openPdfPreview, closePdfPreview }),
    [open, pdf, openPdfPreview, closePdfPreview],
  );

  return (
    <PdfPreviewContext.Provider value={value}>
      {children}
    </PdfPreviewContext.Provider>
  );
}

export function usePdfPreview() {
  const ctx = useContext(PdfPreviewContext);
  if (!ctx) {
    return {
      open: false,
      pdf: null,
      openPdfPreview: () => {},
      closePdfPreview: () => {},
    };
  }
  return ctx;
}

export default PdfPreviewContext;
